import type {
  TableFilterPrimitive,
  TableFilterValue,
  TableFilters,
} from "@/types/tableState.types";
import type { FilterConditionValue } from "@/components/UI/TableToolbar/Filtering/filterTypes";
import { isCategoricalFilter } from "@/components/UI/TableToolbar/Filtering/filtersUtil";

/**
 * `isCategoricalFilter` is the single source of truth for "these are plain
 * values, not a numeric/date condition object", but it is typed against the
 * filter panel's own value union. Table state types the same data more loosely,
 * so narrow once here rather than casting at every call site.
 */
const isCategoricalValues = (
  value: TableFilterValue,
): value is TableFilterPrimitive[] =>
  isCategoricalFilter(value as FilterConditionValue);

/**
 * One column/value pair behind a clicked plot element, taken from the data the
 * element was drawn from: the column is one of the plot's configured scales and
 * the value is whatever the API returned in that column. Nothing here is
 * hand-authored, so a selection cannot drift from what the dataset holds.
 */
export type PlotSelectionCriterion = {
  column: string;
  value: TableFilterPrimitive;
};

/**
 * What a plot emits when the user activates an element. Deliberately a
 * description of *what was selected*, not an instruction to filter: the screen
 * decides where the selection is routed. Today it becomes a screen-wide filter;
 * a later cross-filtering mode can route the same event elsewhere without the
 * draw layer changing.
 */
export type PlotElementSelection = {
  criteria: PlotSelectionCriterion[];
};

const sameValue = (a: unknown, b: TableFilterPrimitive): boolean =>
  // Filter values arrive from the API as strings even where the plot holds a
  // number, so compare on the rendered form rather than by identity.
  String(a) === String(b);

const isUsableValue = (value: TableFilterPrimitive): boolean =>
  value !== null &&
  value !== undefined &&
  `${value}`.trim() !== "" &&
  // "none" is the plot layer's placeholder for an unmapped scale.
  value !== "none";

/**
 * Drop criteria the backend cannot filter on. `filterableColumns` is the set of
 * keys in the dataset's filter metadata, minus any the screen has excluded
 * (pinned filters, the tab's own page-name field). Filtering by a column
 * outside that set would produce a request the backend rejects.
 */
export const getApplicableCriteria = (
  selection: PlotElementSelection,
  filterableColumns: Set<string>,
): PlotSelectionCriterion[] =>
  selection.criteria.filter(
    (criterion) =>
      filterableColumns.has(criterion.column) && isUsableValue(criterion.value),
  );

/**
 * True when every applicable criterion is already present in the filters, i.e.
 * the element the selection came from is the one currently selected.
 */
export const isSelectionApplied = (
  filters: TableFilters,
  selection: PlotElementSelection,
  filterableColumns: Set<string>,
): boolean => {
  const criteria = getApplicableCriteria(selection, filterableColumns);
  if (criteria.length === 0) return false;

  return criteria.every(({ column, value }) => {
    const current = filters[column];
    if (!current || !isCategoricalValues(current)) return false;
    return current.some((existing) => sameValue(existing, value));
  });
};

/**
 * A single-criterion selection is a plain category, and several of them combine
 * correctly as `column IN (a, b)` — so those accumulate.
 *
 * More than one criterion is a conjunction (a stacked bar is "this grade AND
 * this arm"), and the filter model has no way to express a set of conjunctions:
 * storing two of them as independent per-column lists would match their
 * cross-product and return rows the user never selected. Such a selection
 * therefore replaces the filters on the columns it touches, so the filters
 * always describe exactly one selected element.
 */
const accumulates = (criteria: PlotSelectionCriterion[]): boolean =>
  criteria.length === 1;

/**
 * Fold a selection into the screen's filters.
 *
 * Re-selecting an applied selection removes it again, and a column left with no
 * values loses its filter entirely rather than being left as an empty array the
 * backend would have to interpret.
 *
 * Returns the filters unchanged when nothing in the selection is filterable, so
 * callers can treat "no applicable criteria" as an inert click.
 */
export const applySelectionToFilters = (
  filters: TableFilters,
  selection: PlotElementSelection,
  filterableColumns: Set<string>,
): TableFilters => {
  const criteria = getApplicableCriteria(selection, filterableColumns);
  if (criteria.length === 0) return filters;

  const removing = isSelectionApplied(filters, selection, filterableColumns);
  const adding = accumulates(criteria);
  const nextFilters: TableFilters = { ...filters };

  criteria.forEach(({ column, value }) => {
    const current = nextFilters[column];
    // A non-categorical filter on this column (a numeric or date condition)
    // cannot absorb a category, so leave the user's own condition alone.
    if (current && !isCategoricalValues(current)) return;

    const withoutValue = (current ?? []).filter(
      (existing) => !sameValue(existing, value),
    );

    if (removing) {
      if (withoutValue.length === 0) delete nextFilters[column];
      else nextFilters[column] = withoutValue;
      return;
    }

    nextFilters[column] = adding ? [...withoutValue, value] : [value];
  });

  return nextFilters;
};
